import { logPush } from './push-diagnostics';

const SESSION_STORAGE_KEY = 'webviewgold_fcm_token';
const LOCAL_STORAGE_KEY = 'webviewgold_fcm_token';
const MAX_AGE_DAYS = 7;

export type TokenSource = 'memory' | 'session' | 'localStorage' | null;

interface CachedToken {
  token: string;
  timestamp: number;
  platform: string;
}

let inMemoryToken: string | null = null;

export function getCachedToken(): { token: string | null; source: TokenSource } {
  if (inMemoryToken) {
    logPush('info', 'cache', 'Token retrieved from memory cache');
    return { token: inMemoryToken, source: 'memory' };
  }

  try {
    const sessionToken = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (sessionToken) {
      logPush('info', 'cache', 'Token retrieved from sessionStorage');
      inMemoryToken = sessionToken;
      return { token: sessionToken, source: 'session' };
    }
  } catch (error) {
    logPush('warn', 'cache', 'Failed to read from sessionStorage', error);
  }

  try {
    const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (localData) {
      const cached: CachedToken = JSON.parse(localData);
      const ageInDays = (Date.now() - cached.timestamp) / (1000 * 60 * 60 * 24);

      if (ageInDays < MAX_AGE_DAYS) {
        logPush('success', 'cache', `Token retrieved from localStorage (age: ${ageInDays.toFixed(2)} days)`);
        inMemoryToken = cached.token;
        sessionStorage.setItem(SESSION_STORAGE_KEY, cached.token);
        return { token: cached.token, source: 'localStorage' };
      } else {
        logPush('warn', 'cache', `Token in localStorage expired (age: ${ageInDays.toFixed(2)} days)`);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  } catch (error) {
    logPush('warn', 'cache', 'Failed to read from localStorage', error);
  }

  logPush('info', 'cache', 'No cached token found');
  return { token: null, source: null };
}

export function setCachedToken(token: string): void {
  if (!token) {
    logPush('warn', 'cache', 'Attempted to cache empty token');
    return;
  }

  inMemoryToken = token;

  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, token);
    logPush('success', 'cache', 'Token saved to sessionStorage');
  } catch (error) {
    logPush('error', 'cache', 'Failed to save to sessionStorage', error);
  }

  try {
    const cachedData: CachedToken = {
      token,
      timestamp: Date.now(),
      platform: 'ios-webviewgold'
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cachedData));
    logPush('success', 'cache', 'Token saved to localStorage');
  } catch (error) {
    logPush('error', 'cache', 'Failed to save to localStorage', error);
  }
}

export function clearTokenCache(): void {
  inMemoryToken = null;

  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    logPush('info', 'cache', 'Cleared sessionStorage');
  } catch (error) {
    logPush('warn', 'cache', 'Failed to clear sessionStorage', error);
  }

  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    logPush('info', 'cache', 'Cleared localStorage');
  } catch (error) {
    logPush('warn', 'cache', 'Failed to clear localStorage', error);
  }

  logPush('success', 'cache', 'Token cache cleared completely');
}

export function getTokenAge(): number | null {
  try {
    const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (localData) {
      const cached: CachedToken = JSON.parse(localData);
      return Date.now() - cached.timestamp;
    }
  } catch (error) {
    logPush('warn', 'cache', 'Failed to get token age', error);
  }
  return null;
}

export function getTokenAgeInDays(): number | null {
  const age = getTokenAge();
  return age ? age / (1000 * 60 * 60 * 24) : null;
}
